import React, { CSSProperties, FC, ReactNode, memo } from "react";
import { useTheme } from "@mui/material/styles";
import { palette, radius, withAlpha } from "@/themes/tokens";

export type PillTone = "neutral" | "primary" | "success" | "danger" | "info" | "warning" | "dark";

interface PillProps {
    label: ReactNode;
    tone?: PillTone;
    // "soft" is a tinted background with coloured text; "solid" is a filled pill with white text.
    variant?: "soft" | "solid";
    dot?: boolean;
    size?: "regular" | "small";
    style?: CSSProperties;
}

// Small rounded status label, used for auction/lot status, type and deposit chips. Same as the mobile app's Pill.
const Pill: FC<PillProps> = ({ label, tone = "neutral", variant = "soft", dot = false, size = "regular", style }) => {
    const theme = useTheme();
    const color =
        tone === "primary" ? theme.palette.primary.main
            : tone === "success" ? palette.success
                : tone === "danger" ? palette.danger
                    : tone === "info" ? palette.info
                        : tone === "warning" ? palette.warning
                            : tone === "dark" ? palette.ink
                                : palette.textMuted;
    const solid = variant === "solid";
    const small = size === "small";

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                alignSelf: "flex-start",
                gap: small ? 4 : 6,
                padding: small ? "2px 7px" : "4px 10px",
                borderRadius: radius.pill,
                backgroundColor: solid ? color : tone === "neutral" ? palette.surfaceMuted : withAlpha(color, 0.12),
                border: tone === "neutral" && !solid ? `1px solid ${palette.border}` : undefined,
                whiteSpace: "nowrap",
                ...style,
            }}
        >
            {dot && <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: solid ? palette.inverse : color }} />}
            <span style={{ fontSize: small ? 9.5 : 11.5, fontWeight: 600, lineHeight: 1.35, color: solid ? palette.inverse : tone === "neutral" ? palette.text : color }}>
                {label}
            </span>
        </span>
    );
};

export default memo(Pill);
